import React, { useState, useEffect, useCallback } from "react";
import { Route, Routes } from "react-router-dom";

import { logoutUser, updateUser } from "../../redux/actions/user";
import { fbAuth } from "../../services/firebaseService/connection";

import { fbLogoutUser } from "../../services/firebaseService/endPoints/user";
import logger from "../../utils/logger";

import { message, Result, Button, Layout } from "antd";
import LoadingModal from "../commons/LoadingModal";

import ErrorBoundary from "./ErrorBoundary";
import { commonRoutes } from "./Routes/Routes.jsx";
import useAdminDataStore, {
  selectIsDashboardLoading,
  selectDashboardType,
} from "../../store/adminData/adminData";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import GettingStarted from "../GettingStarted/GettingStarted";
import { isMobile } from "../../utils/helper";
import ThemeProvider from "./ThemeProvider";
import SideNav from "./SideNav/SideNav";
import News from "./News/News";
import LoadingOverlay from "../../CommonComponents/LoadingOverlay/LoadingOverlay";
import AnnouncementsManager from "../../components/commons/AnnouncementsManager/AnnouncementsManager";
import useUserStore, { selectUserDetails } from "../../store/user/user.js";
import useDashboardInfo from "../../hooks/useDashboardInfo.js";
import useAppRoutes from "./useAppRoutes.js";
import Header from "./Header/Header.jsx";

const App = () => {
  const [msg, setMsg] = useState("");
  const [width, setWidth] = useState(window.innerWidth);
  const [expanded, setExpanded] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  const user = useUserStore(selectUserDetails);
  const dashboardLoading = useAdminDataStore(selectIsDashboardLoading);
  const dashboardTypeFromStore = useAdminDataStore(selectDashboardType);

  const { routes, isValidRoute } = useAppRoutes(user);

  const { dashboardType, isRegForm, pathName } = useDashboardInfo();
  const noHeader = ["join", "login", "adminlogin"].includes(
    pathName.split("/").pop(),
  );

  const toggleSideNav = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const getHeader = useCallback(() => {
    let style = {
      position: width > 500 ? "fixed" : "relative",
      zIndex: 1000,
      width: "100%",
      marginTop: "-25px",
    };
    if (width > 500) {
      style.height = 80;
    }
    return (
      <div>
        {!noHeader && (
          <Header
            expanded={expanded}
            loading={dashboardLoading}
            toggleSideNav={toggleSideNav}
          />
        )}
      </div>
    );
  }, [width, expanded, dashboardLoading, pathName, toggleSideNav]);

  const getSideNav = useCallback(() => {
    if (!user.isAuthenticated) {
      return null;
    }

    if (!isValidRoute) {
      return null;
    }

    const handleSideNavCollapse = (value) => {
      setExpanded(!value);
    };

    return (
      <SideNav
        expanded={expanded}
        loading={dashboardLoading}
        handleSideNavCollapse={handleSideNavCollapse}
      />
    );
  }, [user.isAuthenticated, isValidRoute, expanded, dashboardLoading]);

  const calculateMainLayoutMargin = useCallback(() => {
    if (!user.isAuthenticated || !isValidRoute) {
      return 0;
    }

    if (!expanded) {
      return width < 500 ? 0 : 80;
    }

    return isMobile() ? 0 : 215;
  }, [user.isAuthenticated, isValidRoute, expanded, width]);

  // Auth setup - runs once on mount
  useEffect(() => {
    if (isRegForm) {
      return;
    }

    const setupAuth = async () => {
      // Dev-only: bypass login on localhost using a custom token from localStorage.
      // Generate with: node scripts/generate-auth-token.js <uid>
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";
      const devToken = isLocalhost && localStorage.getItem("dev_auth_token");
      if (!fbAuth.currentUser && devToken) {
        try {
          await signInWithCustomToken(fbAuth, devToken);
          localStorage.removeItem("dev_auth_token");
          updateUser();
          return;
        } catch (e) {
          console.error("Dev auth token failed:", e);
          localStorage.removeItem("dev_auth_token");
        }
      }

      let c_name = "auth_token";

      let token = document.cookie.split("; ").reduce((r, v) => {
        const parts = v.split("=");
        return parts[0] === c_name ? decodeURIComponent(parts[1]) : r;
      }, "");

      document.cookie = `auth_token=${token}; domain=.chant.fan; expires=Thu, 01 Jan 1970 00:00:00; path=/`;

      if (!fbAuth.currentUser && token) {
        try {
          await signInWithCustomToken(fbAuth, token);
          updateUser();
        } finally {
          window.location.replace("/admin/directory/group");
        }
      }

      const unsubscribe = onAuthStateChanged(fbAuth, (authUser) => {
        logger.log(
          "[emailAuth] onAuthStateChanged fired. uid:",
          authUser?.uid,
          "| authUser present:",
          !!authUser,
        );
        setAuthInitialized(true);

        if (authUser) {
          if (
            (authUser.email || authUser.phoneNumber) &&
            window.location.pathname !== "/register"
          ) {
            updateUser();
          }
        } else {
          logoutUser();
        }
      });

      return unsubscribe;
    };

    const cleanupPromise = setupAuth();

    return () => {
      cleanupPromise.then((unsubscribe) => {
        if (unsubscribe) {
          unsubscribe();
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial setup for mobile and path

  // Network and resize event listeners
  useEffect(() => {
    if (isMobile()) {
      setExpanded(false);
    }

    if (dashboardType === "admin" && !isRegForm) {
      useAdminDataStore.getState().setRedirectPath(pathName);
    }
    const handleOffline = () => {
      const offlineMsg = message.warning(
        "Your device lost its internet connection",
        0,
      );
      setMsg(offlineMsg);
    };

    const handleOnline = () => {
      if (msg) {
        msg();
        message.success("Your device is connected to the internet");
        setMsg("");
      }
    };

    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL dashboard with store state
  useEffect(() => {
    if (dashboardType && dashboardType !== dashboardTypeFromStore) {
      useAdminDataStore.getState().setDashboardType(dashboardType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardType]);

  if (user.isAuthenticated === "unauthorized") {
    setTimeout(() => {
      fbLogoutUser();
    }, 0);
    return (
      <Result
        status="403"
        title="403"
        subTitle="You are not authorized to access this page"
        extra={
          <Button type="primary" onClick={fbLogoutUser}>
            Go Back
          </Button>
        }
      />
    );
  }

  const appLoading =
    (!authInitialized || user.isAuthenticated === undefined) && !isRegForm;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <LoadingModal loading={appLoading} message="Loading dashboard..." />
      <News />

      <Layout style={{ backgroundColor: "#ffffff" }}>
        <ErrorBoundary>
          <ThemeProvider>
            {getHeader()}

            <Layout
              style={{
                flexDirection: "row",
                marginTop: noHeader ? 0 : "var(--primary-header-height)",
                backgroundColor: "#ffffff",
              }}
            >
              {getSideNav()}

              {dashboardLoading && (
                <LoadingOverlay marginLeft={calculateMainLayoutMargin()} />
              )}
              <Layout style={{ marginLeft: calculateMainLayoutMargin() }}>
                <div
                  style={{
                    minHeight: noHeader
                      ? "100vh"
                      : "calc(100vh - var(--primary-header-height))",
                    overflow: "auto",
                    backgroundColor: !user.isAuthenticated
                      ? "transparent"
                      : "var(--primary-gray)",
                  }}
                >
                  {!appLoading && (
                    <Routes>
                      {routes.map((element) => {
                        return <Route {...element} key={element.path} />;
                      })}
                      {commonRoutes.map((element) => {
                        return <Route {...element} key={element.path} />;
                      })}
                    </Routes>
                  )}
                </div>
              </Layout>
            </Layout>
            {user.isAuthenticated &&
              user.role === "admin" &&
              dashboardType === "admin" &&
              !isMobile() && <GettingStarted />}
          </ThemeProvider>

          <AnnouncementsManager />
        </ErrorBoundary>
      </Layout>
    </div>
  );
};

export default App;
